import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
  Typography,
  Collapse,
  alpha,
  Tooltip,
} from "@mui/material";
import {
  Home,
  Dashboard,
  DocumentScanner,
  MenuBook,
  Summarize,
  People,
  Business,
  Help,
  Computer,
  Dvr,
  RecentActors,
  Storefront,
  Inventory2,
  PictureAsPdf,
  SyncAlt,
  ManageSearch,
  Mail,
  ChevronRight,
  ExpandMore,
  MenuOpen,
  Menu,
  Logout,
  ProductionQuantityLimits,
  ProductionQuantityLimitsSharp,
  PeopleAltSharp,
  AutoAwesomeMosaic,
  Phone,
  Equalizer,
  SmartToy,
  Map,
  Money,
} from "@mui/icons-material";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import { useDrawer } from "../../DrawerContext";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt';
import { adminModuleVersion } from "../../Helpers/appConfig";
import { Currency } from "lucide-react";

const COLORS = {
  background: "rgba(4, 18, 31, 0.75)",
  primary: "#38bdf8",
  hover: "rgba(255, 255, 255, 0.05)",
  active: "rgba(56, 189, 248, 0.12)",
  text: {
    primary: "#ffffff",
    secondary: "rgba(255, 255, 255, 0.65)",
    muted: "rgba(255, 255, 255, 0.4)",
  },
  border: "rgba(255, 255, 255, 0.08)",
};

const menuItems = [
  { text: "Home", icon: <Home />, path: "/admin/home" },
  { text: "Dashboard", icon: <Equalizer />, path: "/admin/dashboard2" },
  { text: "Dynamic Dashboard", icon: <AutoAwesomeMosaic />, path: "/admin/dynamic-dashboard" },
  { text: "User's Summary", icon: <Dashboard />, path: "/admin/dashboard" },
  {
    text: "Quotations",
    icon: <DocumentScanner />,
    subMenu: [
      { text: "All Quotations", icon: <MenuBook sx={{ fontSize: "18px" }} />, path: "/admin/projects" },
      { text: "Quotation Report", icon: <Summarize sx={{ fontSize: "18px" }} />, path: "/admin/projects/report" },
      { text: "Product Log", icon: <ShoppingCartIcon sx={{ fontSize: "18px" }} />, path: "/admin/product-log" },
    ],
  },
  { text: "Users", icon: <People />, path: "/admin/userlist" },
  { text: "Clients", icon: <Business />, path: "/admin/clientlist" },
  { text: "Customer", icon: <PeopleAltSharp />, path: "/admin/customer" },
  { text: "Helpdesk", icon: <Help />, path: "/admin/help/desks" },
  {
    text: "System Logs",
    icon: <Computer />,
    subMenu: [
      { text: "Activity Log", icon: <Dvr sx={{ fontSize: "18px" }} />, path: "/admin/log" },
      { text: "Log History", icon: <RecentActors sx={{ fontSize: "18px" }} />, path: "/admin/log/details" },
      { text: "Device Log", icon: <AppSettingsAltIcon sx={{ fontSize: "18px" }} />, path: "/admin/device/log" },
    ],
  },
  {
    text: "Products",
    icon: <Storefront />,
    subMenu: [
      { text: "All Products", icon: <Storefront sx={{ fontSize: "18px" }} />, path: "/admin/product" },
      { text: "Inventory", icon: <Inventory2 sx={{ fontSize: "18px" }} />, path: "/admin/inventory" },
      { text: "Smart Suggestions", icon: <AutoAwesomeMosaic sx={{ fontSize: "18px" }} />, path: "/admin/smart-suggestions" },
    ],
  }, 
  { text: "AI Management", icon: <SmartToy />, path: "/admin/ai-management" },
  { text: "Map", icon: <Map />, path: "/admin/map" },
  { text: "PDF Layout", icon: <PictureAsPdf />, path: "/admin/cover-images" },
  {
    text: "Engagement",
    icon: <Mail />,
    subMenu: [
      { text: "Activity", icon: <ScheduleSendIcon sx={{ fontSize: "18px" }} />, path: "/admin/users-engagement" },
      { text: "Mails Log", icon: <AttachEmailIcon sx={{ fontSize: "18px" }} />, path: "/admin/mails" },
    ],
  },
  {
    text: "Data Transfer",
    icon: <SyncAlt />,
    subMenu: [
      { text: "Transfer Data", icon: <SyncAlt sx={{ fontSize: "18px" }} />, path: "/admin/transfer" },
      { text: "Transfer History", icon: <ManageSearch sx={{ fontSize: "18px" }} />, path: "/admin/transfer/history" },
    ],
  },
  { text: "Currency", icon: <Money sx={{ fontSize: "18px" }} />, path: "/admin/currency" },

];

const Sidebar = ({disableMenu}) => {
  const { drawerVisible, setDrawerVisible } = useDrawer();
  const location = useLocation();
  const navigate = useNavigate();

  const drawerWidth = drawerVisible ? 240 : 80;

  const handleLogout = () => {
    Cookies.remove("token");
    localStorage.removeItem("u_token");
    localStorage.removeItem("role_code");
    navigate("/admin/login");
  };

  const toggleMenu = () => setDrawerVisible(!drawerVisible);

  const [expandedMenus, setExpandedMenus] = useState(() => {
    const initialOpen = {};
    menuItems.forEach((item) => {
      if (item.subMenu?.some((sub) => sub.path === location.pathname)) {
        initialOpen[item.text] = true;
      }
    });
    return initialOpen;
  });

  const handleMenuToggle = (menuText) => {
    setExpandedMenus((prev) => ({ ...prev, [menuText]: !prev[menuText] }));
  };

  const isActive = (path) => location.pathname === path;
  const isSubMenuActive = (subItems) => subItems?.some((item) => isActive(item.path));

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          backgroundColor: COLORS.background,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderRight: `1px solid ${COLORS.border}`,
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
          color: COLORS.text.primary,
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
    >
      <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: drawerVisible ? "space-between" : "center" }}>
        {drawerVisible && (
          <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1rem', color: COLORS.primary, letterSpacing: 1 }}>
            ADMIN <span style={{ color: '#fff' }}>PANEL</span>
          </Typography>
        )}
        <IconButton onClick={toggleMenu} sx={{ color: COLORS.text.secondary }} disabled={disableMenu}>
          {drawerVisible ? <MenuOpen /> : <Menu />}
        </IconButton>
      </Box>

      <List sx={{ px: 1, py: 1, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const isItemActive = isActive(item.path) || isSubMenuActive(item.subMenu);
          const isExpanded = expandedMenus[item.text];

          return (
            <React.Fragment key={item.text}>
              <Tooltip title={!drawerVisible ? item.text : ""} placement="right" arrow>
                <ListItem
                  button
                  component={item.subMenu ? "div" : Link}
                  to={item.subMenu ? undefined : item.path}
                  onClick={item.subMenu ? () => handleMenuToggle(item.text) : undefined}
                  sx={{
                    borderRadius: "10px",
                    px: drawerVisible ? 2 : 0,
                    py: 1,
                    mb: 0.3,
                    cursor: "pointer",
                    backgroundColor: isItemActive ? COLORS.active : "transparent",
                    borderLeft: isItemActive ? `3px solid ${COLORS.primary}` : "3px solid transparent",
                    justifyContent: drawerVisible ? "flex-start" : "center",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(56, 189, 248, 0.07)",
                      borderLeft: isItemActive ? `3px solid ${COLORS.primary}` : `3px solid rgba(56, 189, 248, 0.3)`,
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: drawerVisible ? "35px" : "auto",
                    color: isItemActive ? COLORS.primary : COLORS.text.secondary
                  }}>
                    {item.icon}
                  </ListItemIcon>

                  {drawerVisible && (
                    <>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: "0.85rem",
                          fontWeight: isItemActive ? 700 : 500,
                          color: isItemActive ? "#fff" : COLORS.text.secondary,
                          sx: { whiteSpace: "nowrap" }
                        }}
                      />
                      {item.subMenu && (
                        <ExpandMore sx={{
                          fontSize: "1.1rem",
                          transition: "0.3s",
                          transform: isExpanded ? "rotate(180deg)" : "none"
                        }} />
                      )}
                    </>
                  )}
                </ListItem>
              </Tooltip>

              {item.subMenu && drawerVisible && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List sx={{ pl: 4, py: 0.5 }}>
                    {item.subMenu.map((subItem) => (
                      <ListItem
                        key={subItem.text}
                        component={Link}
                        to={subItem.path}
                        sx={{
                          borderRadius: "8px",
                          mb: 0.3,
                          py: 0.8,
                          cursor: "pointer",
                          color: isActive(subItem.path) ? COLORS.primary : COLORS.text.secondary,
                          transition: "all 0.2s ease",
                          "&:hover": { color: "#fff", bgcolor: "rgba(56, 189, 248, 0.07)" },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: "30px", color: "inherit" }}>{subItem.icon}</ListItemIcon>
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: isActive(subItem.path) ? 700 : 400, sx: { whiteSpace: "nowrap" } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 1.5, pb: 2 }}>
        <Divider sx={{ borderColor: COLORS.border, mb: 2 }} />
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            borderRadius: "12px",
            bgcolor: alpha("#ef4444", 0.08),
            color: "#ef4444",
            px: drawerVisible ? 2 : 0,
            justifyContent: drawerVisible ? "flex-start" : "center",
            "&:hover": { bgcolor: alpha("#ef4444", 0.15) },
          }}
        >
          <ListItemIcon sx={{ minWidth: drawerVisible ? "35px" : "auto", color: "inherit" }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          {drawerVisible && (
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 700 }}
            />
          )}
        </ListItem>
      </Box>

      <Box sx={{ textAlign: "center", pb: 2 }}>
        <Typography variant="caption" sx={{ color: COLORS.text.muted, fontWeight: 700, fontSize: '0.65rem' }}>
          V-{adminModuleVersion}
        </Typography>
      </Box>
    </Drawer>
  );
};

export default Sidebar;